import {Block} from '../models/Block';
import {Response, Result} from '../network/models/Response';
import {Presenter} from '../support/with_presenter';
import {firstValueFrom} from 'rxjs';
import {BlocksRepository} from '../business/blocks/BlocksRepository';
import {EventsRepository} from '../business/events/EventsRepository';
import {PartEvent} from '../models/PartEvent';
import {MediaItem} from '../models/MediaItem';
import {MediaItemsRepository} from '../business/media/MediaItemsRepository';
import {Chapter} from '../models/Chapter';
import {ChaptersRepository} from '../business/chapters/ChaptersRepository';
import {DeviceRequirementRule} from '../models/DeviceRequirementRule';

export interface TrainingPlanSaver {
    save(
        title: string,
        description: string | null,
        deviceRequirementRules: DeviceRequirementRule[],
        isPublished: boolean,
        blocks: Block[],
        events: PartEvent[],
        chapters: Chapter[],
        mediaItems: MediaItem[],
    ): Promise<Response<{redirectTo: string}>>;
}

export enum Views {
    Structure,
    Timeline,
    Media,
}

export class TrainingPlanEditorPresenter implements Presenter {
    public error: boolean = false;
    public errors: Array<{field: string; error: string}> = [];

    public title: string = '';
    public description: string = '';
    public deviceRequirementRules: DeviceRequirementRule[] = [];

    public activeView = Views.Structure;

    constructor(
        initialTitle: string,
        initialDescription: string,
        initialDeviceRequirementRules: DeviceRequirementRule[],
        private initialIsPublished: boolean,
        private trainingPlanSaver: TrainingPlanSaver,
        private blocksRepository: BlocksRepository,
        private eventsRepository: EventsRepository,
        private chaptersRepository: ChaptersRepository,
        private mediaItemsRepository: MediaItemsRepository,
    ) {
        this.title = initialTitle;
        this.description = initialDescription;
        this.deviceRequirementRules = initialDeviceRequirementRules;
    }

    public mount(): void {
        /* Noop */
    }

    public unmount(): void {
        /* Noop */
    }

    public onChangeTitle(value: string) {
        this.title = value;
    }

    public onChangeDescription(value: string) {
        this.description = value;
    }

    public onChangeDeviceRequirementRules(value: DeviceRequirementRule[]) {
        this.deviceRequirementRules = value;
    }

    public async onSubmit() {
        try {
            this.error = false;
            this.errors = [];
            const result = await this.trainingPlanSaver.save(
                this.title,
                this.description,
                this.deviceRequirementRules,
                this.initialIsPublished,
                await firstValueFrom(this.blocksRepository.blocks),
                await firstValueFrom(this.eventsRepository.events),
                await firstValueFrom(this.chaptersRepository.chapters),
                await firstValueFrom(this.mediaItemsRepository.mediaItems),
            );
            switch (result.result) {
                case Result.Success:
                    window.location.href = result.data?.redirectTo ?? '/training/me/plans';
                    break;
                case Result.Validation:
                    this.errors = result.errors;
                    break;
                case Result.Error:
                    this.error = true;
                    break;
            }
        } catch (e) {
            this.error = true;
        }
    }

    public getErrorForField(field: string) {
        const error = this.errors.find((e) => e.field === field);
        return error !== undefined ? error.error : null;
    }

    public changeView(view: Views) {
        this.activeView = view;
    }
}
